import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip, Chip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, CloudUpload as CloudUploadIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import usuarioService, { Usuario } from '../../services/usuarioService';
import { useAuth } from '../../contexts/AuthContext';
import { canManageSistemaUsers, effectiveUserRoles } from '../../utils/sistemaAuthz';
import * as XLSX from 'xlsx';

const Usuarios: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [_loading, setLoading] = useState(false);

  const fetchUsuarios = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const usuarios = await usuarioService.getUsuarios({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search
      });
      console.log('Usuarios recibidos:', usuarios);
      setUsuarios(usuarios);
      setTotalCount(usuarios.length);
    } catch (error: any) {
      console.error('Error al cargar usuarios:', error);
      enqueueSnackbar('Error al cargar usuarios', { variant: 'error' });
      setUsuarios([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const canList = canManageSistemaUsers(effectiveUserRoles(user?.roles));

  useEffect(() => {
    if (!canList) return;
    fetchUsuarios();
  }, [canList]);

  useEffect(() => {
    if (!canList) return;
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchUsuarios(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, canList]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchUsuarios(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchUsuarios(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (username: string) => {
    if (window.confirm('¿Está seguro de eliminar este usuario?')) {
      try {
        await usuarioService.deleteUsuario(username);
        enqueueSnackbar('Usuario eliminado exitosamente', { variant: 'success' });
        fetchUsuarios();
      } catch (error: any) {
        console.error('Error al eliminar usuario:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar usuario', { variant: 'error' });
      }
    }
  };

  const handleExportExcel = async () => {
    try {
      const allUsuarios = await usuarioService.getUsuarios({
        pageNumber: 1,
        pageSize: 10000,
        searchTerm: searchTerm
      });
      const dataToExport = allUsuarios.map((u: any) => ({
      'Usuario': u.id,
      'Nombre': u.username,
      'Email': u.email,
      'Rol': u.role,
      'Fecha Creación': u.createdAt,
      'Último Login': u.lastLogin
    }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
      XLSX.writeFile(wb, 'Usuarios.xlsx');
      enqueueSnackbar('Archivo exportado exitosamente', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al exportar datos', { variant: 'error' });
    }
  };

  const handleImportExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        let successCount = 0;
        let errorCount = 0;

        for (const row of jsonData as any[]) {
          try {
            const usuario: Usuario = {
              usuario: row['Usuario'] || row['username'] || '',
              username: row['Usuario'] || row['username'],
              email: row['Email'] || row['email'],
              role: row['Rol'] || row['role'] || 'User',
              password: row['Password'] || row['password'] || 'temporal123'
            };

            await usuarioService.createUsuario(usuario);
            successCount++;
          } catch (error) {
            errorCount++;
            console.error('Error al importar usuario:', error);
          }
        }

        enqueueSnackbar(`Importación completada: ${successCount} exitosos, ${errorCount} errores`, { 
          variant: errorCount > 0 ? 'warning' : 'success' 
        });
        fetchUsuarios();
      } catch (error) {
        enqueueSnackbar('Error al procesar el archivo Excel', { variant: 'error' });
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  if (!canList) {
    return <Navigate to="/menu-principal" replace />;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Usuarios
            </Typography>
            <Stack direction="row" spacing={1}>
              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="import-excel-usuarios"
                type="file"
                onChange={handleImportExcel}
              />
              <label htmlFor="import-excel-usuarios">
                <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="small">
                  Importar
                </Button>
              </label>
              <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={handleExportExcel} size="small">
                Exportar
              </Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/sistema/usuarios/nuevo')}>
                Nuevo Usuario
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por usuario, email o rol..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                }
              }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {usuarios.map((usuario) => (
                <Card key={usuario.username} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Usuario: {usuario.usuario || usuario.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nombre: {usuario.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usuario.email}
                    </Typography>
                    <Chip label={usuario.role} size="small" color="primary" sx={{ width: 'fit-content' }} />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => {
                        const userId = usuario.id || (usuario as any).Id || (usuario as any).usuario || (usuario as any).Usuario || '';
                        console.log('Navegando con ID:', userId);
                        navigate(`/sistema/usuarios/${encodeURIComponent(userId)}`);
                      }} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(usuario.id || (usuario as any).Id || (usuario as any).usuario || (usuario as any).Usuario || '')} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rol</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha Creación</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usuarios.map((usuario) => (
                    <TableRow key={usuario.username} hover>
                      <TableCell>{usuario.usuario || usuario.id}</TableCell>
                      <TableCell>{usuario.username}</TableCell>
                      <TableCell>{usuario.email}</TableCell>
                      <TableCell>
                        <Chip label={usuario.role} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        {usuario.createdAt ? new Date(usuario.createdAt).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => {
                            const userId = usuario.id || (usuario as any).Id || (usuario as any).usuario || (usuario as any).Usuario || '';
                            console.log('Navegando con ID:', userId);
                            navigate(`/sistema/usuarios/${encodeURIComponent(userId)}`);
                          }} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(usuario.id || (usuario as any).Id || (usuario as any).usuario || (usuario as any).Usuario || '')} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Usuarios;
